import React, { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiCalendar, FiRefreshCw, FiUsers } from 'react-icons/fi';
import Layout from '../components/Layout';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Holidays.css';

const TYPE_STYLE = {
  national: { label: 'National',  bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', emoji: '🇮🇳' },
  regional: { label: 'Regional',  bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', emoji: '🏛️' },
  company:  { label: 'Company',   bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff', emoji: '🏢' },
  optional: { label: 'Optional',  bg: '#fffbeb', color: '#d97706', border: '#fde68a', emoji: '⭐' },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const currentYear = new Date().getFullYear();

const weekday = (d) => new Date(d).toLocaleDateString('en-IN', { weekday: 'long' });
const daysLeft = (d) => {
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (diff < 0) return null;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return `${diff} days`;
};

// Indian public holidays seed data
const INDIA_HOLIDAYS_2026 = [
  { name: "New Year's Day",        date: '2026-01-01', type: 'national' },
  { name: 'Makar Sankranti',       date: '2026-01-14', type: 'regional' },
  { name: 'Republic Day',          date: '2026-01-26', type: 'national' },
  { name: 'Holi',                  date: '2026-03-04', type: 'national' },
  { name: 'Good Friday',           date: '2026-04-03', type: 'national' },
  { name: 'Ram Navami',            date: '2026-03-28', type: 'national' },
  { name: 'Ambedkar Jayanti',      date: '2026-04-14', type: 'national' },
  { name: 'Eid ul-Fitr',           date: '2026-03-31', type: 'national' },
  { name: 'Maharashtra Day',       date: '2026-05-01', type: 'regional' },
  { name: 'Eid ul-Adha',           date: '2026-06-07', type: 'national' },
  { name: 'Independence Day',      date: '2026-08-15', type: 'national' },
  { name: 'Janmashtami',           date: '2026-08-23', type: 'national' },
  { name: 'Gandhi Jayanti',        date: '2026-10-02', type: 'national' },
  { name: 'Dussehra',              date: '2026-10-21', type: 'national' },
  { name: 'Diwali',                date: '2026-11-08', type: 'national' },
  { name: 'Guru Nanak Jayanti',    date: '2026-11-23', type: 'national' },
  { name: 'Christmas',             date: '2026-12-25', type: 'national' },
];

export default function Holidays() {
  const { user } = useAuth();
  const isAdmin = user?.role?.isAdmin;

  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [year, setYear] = useState(currentYear);
  const [seeding, setSeeding] = useState(false);
  const [fillingId, setFillingId] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', type: 'national', description: '', isRecurring: false });
  const [confirm, setConfirm] = useState(null);

  const fetchHolidays = useCallback(() => {
    API.get('/holidays', { params: { year } }).then(r => setHolidays(r.data)).catch(() => {});
  }, [year]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const openModal = (h = null) => {
    setEditing(h);
    setForm(h ? {
      name: h.name, date: h.date?.slice(0, 10), type: h.type,
      description: h.description || '', isRecurring: h.isRecurring || false,
    } : { name: '', date: '', type: 'national', description: '', isRecurring: false });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/holidays/${editing._id}`, form); toast.success('Holiday updated'); }
      else { await API.post('/holidays', form); toast.success('Holiday added'); }
      setShowModal(false); fetchHolidays();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = (h) => setConfirm({ type: 'delete', holiday: h });
  const doDelete = async () => {
    const h = confirm.holiday; setConfirm(null);
    try { await API.delete(`/holidays/${h._id}`); toast.success('Deleted'); fetchHolidays(); }
    catch { toast.error('Error deleting'); }
  };

  const handleFillHoliday = (h) => setConfirm({ type: 'fill', holiday: h });
  const doFillHoliday = async () => {
    const h = confirm.holiday; setConfirm(null);
    setFillingId(h._id);
    try {
      const res = await API.post('/attendance/fill-holiday', { date: h.date, holidayName: h.name });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'Error filling attendance'); }
    finally { setFillingId(null); }
  };

  const handleSeedIndia = async () => {
    setSeeding(true);
    try {
      // Reuse month/day from 2026 template but replace the year with the current `year` state
      const seedHolidays = INDIA_HOLIDAYS_2026.map(h => ({
        ...h,
        date: h.date.replace('2026', String(year)),
      }));
      const alreadyPresent = new Set(
        holidays.map(h => `${h.name}|${new Date(h.date).toISOString().slice(0, 10)}`)
      );
      const toAdd = seedHolidays.filter(h => !alreadyPresent.has(`${h.name}|${h.date}`));
      if (!toAdd.length) {
        toast.info(`Indian holidays for ${year} are already added`);
        return;
      }
      await Promise.all(toAdd.map(h => API.post('/holidays', h).catch(() => {})));
      toast.success(`Indian holidays for ${year} added!`);
      fetchHolidays();
    } catch { toast.error('Error seeding holidays'); }
    finally { setSeeding(false); }
  };

  const filtered = holidays.filter(h => {
    const matchSearch = !search || h.name.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || h.type === filterType;
    const matchMonth = !filterMonth || new Date(h.date).getMonth() === Number(filterMonth);
    return matchSearch && matchType && matchMonth;
  });

  // Group by month for calendar view
  const byMonth = MONTHS.reduce((acc, m, i) => {
    const items = filtered.filter(h => new Date(h.date).getMonth() === i);
    if (items.length) acc[i] = items;
    return acc;
  }, {});

  // Stats
  const upcoming = holidays.filter(h => new Date(h.date) >= new Date()).length;
  const past = holidays.length - upcoming;

  return (
    <Layout>
      <div className="page">
        <div className="page-actions">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isAdmin && (
              <button className="hol-seed-btn" onClick={handleSeedIndia} disabled={seeding}>
                🇮🇳 {seeding ? 'Adding...' : `Add India Holidays ${year}`}
              </button>
            )}
            {isAdmin && (
              <button className="btn-primary-sm" onClick={() => openModal()}><FiPlus /> Add Holiday</button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="hol-stats">
          <div className="hol-stat-card" style={{ borderTop: '3px solid #3b82f6' }}>
            <p className="hol-stat-num" style={{ color: '#3b82f6' }}>{holidays.length}</p>
            <p className="hol-stat-label">Total Holidays</p>
          </div>
          <div className="hol-stat-card" style={{ borderTop: '3px solid #16a34a' }}>
            <p className="hol-stat-num" style={{ color: '#16a34a' }}>{upcoming}</p>
            <p className="hol-stat-label">Upcoming</p>
          </div>
          <div className="hol-stat-card" style={{ borderTop: '3px solid #94a3b8' }}>
            <p className="hol-stat-num" style={{ color: '#94a3b8' }}>{past}</p>
            <p className="hol-stat-label">Past</p>
          </div>
          {Object.entries(TYPE_STYLE).map(([key, st]) => (
            <div key={key} className="hol-stat-card" style={{ borderTop: `3px solid ${st.color}` }}>
              <p className="hol-stat-num" style={{ color: st.color }}>{holidays.filter(h => h.type === key).length}</p>
              <p className="hol-stat-label">{st.emoji} {st.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="hol-filters">
          <div className="search-box">
            <FiSearch />
            <input placeholder="Search holidays..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="al-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(TYPE_STYLE).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
          </select>
          <select className="al-select" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            <option value="">All Months</option>
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <div className="hol-year-nav">
            <button onClick={() => setYear(y => y - 1)}>‹</button>
            <span>{year}</span>
            <button onClick={() => setYear(y => y + 1)}>›</button>
          </div>
          <button className="al-select" style={{ cursor: 'pointer' }} onClick={fetchHolidays}><FiRefreshCw size={13} /></button>
          <span className="result-count" style={{ marginLeft: 'auto' }}>{filtered.length} holiday{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Calendar grouped view */}
        {Object.keys(byMonth).length === 0 ? (
          <div className="hol-empty">
            <div style={{ fontSize: '3rem' }}>🗓️</div>
            <h3>No holidays found</h3>
            {isAdmin && holidays.length === 0 && (
              <p>Click <strong>"Add India Holidays"</strong> to quickly add all Indian public holidays for {year}.</p>
            )}
          </div>
        ) : (
          <div className="hol-months">
            {Object.entries(byMonth).map(([monthIdx, items]) => (
              <div key={monthIdx} className="hol-month-block">
                <div className="hol-month-header">
                  <FiCalendar size={14} />
                  <span>{MONTHS[monthIdx]} {year}</span>
                  <span className="hol-month-count">{items.length}</span>
                </div>
                <div className="hol-month-list">
                  {items.map(h => {
                    const st = TYPE_STYLE[h.type] || TYPE_STYLE.national;
                    const left = daysLeft(h.date);
                    const isPast = new Date(h.date) < new Date();
                    return (
                      <div key={h._id} className={`hol-item ${isPast ? 'past' : ''}`}>
                        <div className="hol-date-box" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                          <span className="hol-date-day">{new Date(h.date).getDate()}</span>
                          <span className="hol-date-month">{MONTHS[new Date(h.date).getMonth()]}</span>
                        </div>
                        <div className="hol-item-body">
                          <div className="hol-item-top">
                            <p className="hol-item-name">{h.name}</p>
                            <span className="hol-type-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                              {st.emoji} {st.label}
                            </span>
                          </div>
                          <div className="hol-item-meta">
                            <span>{weekday(h.date)}</span>
                            {h.isRecurring && <span className="hol-recurring">↺ Recurring</span>}
                            {h.description && <span className="hol-desc">{h.description}</span>}
                          </div>
                        </div>
                        <div className="hol-item-right">
                          {left && <span className={`hol-days-left ${left === 'Today' ? 'today' : ''}`}>{left}</span>}
                          {isAdmin && (
                            <div className="hol-item-actions">
                              <button
                                className="hol-fill-btn"
                                onClick={() => handleFillHoliday(h)}
                                disabled={fillingId === h._id}
                                title="Fill holiday attendance for all employees"
                              >
                                <FiUsers size={13} />
                                {fillingId === h._id ? 'Filling...' : 'Fill Attendance'}
                              </button>
                              <div className="td-actions">
                                <button className="action-btn edit" onClick={() => openModal(h)}><FiEdit2 /></button>
                                <button className="action-btn delete" onClick={() => handleDelete(h._id)}><FiTrash2 /></button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {confirm?.type === 'delete' && (
          <div className="modal-overlay" onClick={() => setConfirm(null)}>
            <div className="modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
              <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Delete Holiday</h3>
              <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Delete "{confirm.holiday.name}"? This cannot be undone.</p>
              <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn-danger" style={{ flex: 1 }} onClick={doDelete}>Delete</button>
              </div>
            </div>
          </div>
        )}
        {confirm?.type === 'fill' && (
          <div className="modal-overlay" onClick={() => setConfirm(null)}>
            <div className="modal" style={{ maxWidth: 400, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📋</div>
              <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Fill Holiday Attendance</h3>
              <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>
                Mark all employees as <strong>Holiday</strong> for "{confirm.holiday.name}" ({new Date(confirm.holiday.date).toLocaleDateString('en-IN')})?
              </p>
              <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn-primary" style={{ flex: 1 }} onClick={doFillHoliday}>Fill Attendance</button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? 'Edit Holiday' : 'Add Holiday'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Holiday Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Diwali" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      {Object.entries(TYPE_STYLE).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                  <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." />
                </div>
                <label className="hol-recurring-label">
                  <input type="checkbox" checked={form.isRecurring} onChange={e => setForm({ ...form, isRecurring: e.target.checked })} />
                  Recurring every year
                </label>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">{editing ? 'Update' : 'Add Holiday'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
